import {
  analyzeGeometryReadiness,
  analyzeNoiseScreening,
  analyzeSunShadowScreening,
  analyzeWindComfortScreening,
  buildGeometryExchange,
  buildProjectSnapshot,
  collectNativeScreening,
  collectWindComfortScreening,
  DEFAULT_WIND_COMFORT_SCALE,
  loadAutoFormaProjectSnapshotClient,
  Pipeline,
  type Building,
  type FormaGeometryExchangeClient,
  type FormaNativeAnalysisClient,
  type FormaPredictiveAnalysisGroundGrid,
  type FormaProjectSnapshotClient,
  type FormaWindPredictionClient,
  type GeometryExchange,
  type MunicipalityId,
  type NativeNoiseScreeningRequest,
  type NativeScreeningResult,
  type NativeSunScreeningRequest,
  type NoiseAnalysisResult,
  type Project,
  type ResultsSummary,
  type SimulationEvidence,
  type SunAnalysisResult,
  type WindComfortScale,
  type WindComfortScreeningResult,
} from '@enersu/core-orchestrator';
import { listPilotComplianceStudies } from '@enersu/israel-compliance';
import {
  buildHebrewReportShell,
  buildReportPackageFromEvidence,
  collectedStudiesToReportAnalyses,
  HEBREW_REPORT_MESSAGES,
  pipelineResultsToAnalyses,
  type GenericAnalysisOutput,
  type ReportAgentPackage,
} from '@enersu/report-generator';

export interface RunLocalPilotPipelineOptions {
  buildingUseType: Building['useType'];
  forma: FormaProjectSnapshotClient | undefined;
  geometryClient?: FormaGeometryExchangeClient;
  municipalityId: MunicipalityId;
  projectName?: string;
  /**
   * Optional pre-fetched Forma sun analysis. When provided it is normalized
   * into `sun-shadow-screening` evidence and flows into the report package and
   * shell, so the pilot can surface sun/shadow screening without wiring the
   * live analysis trigger here.
   */
  sunAnalysis?: SunAnalysisResult;
  /** Optional pre-fetched Forma noise analysis, normalized into `noise` evidence. */
  noiseAnalysis?: NoiseAnalysisResult;
  /**
   * Optional pre-fetched Forma rapid wind-comfort grid (plus optional comfort
   * scale), normalized into `wind-comfort` evidence.
   */
  windComfort?: { grid: FormaPredictiveAnalysisGroundGrid; comfortScale?: WindComfortScale };
  /**
   * Optional live native-screening request. When set, the pipeline triggers the
   * requested Forma native analyses (sun reuse-first, noise) against the
   * project's selected building paths and feeds their results into screening
   * evidence. A pre-fetched `sunAnalysis`/`noiseAnalysis` override takes
   * precedence for the same study, so no live trigger is issued for it.
   */
  nativeScreening?: {
    /** Connected Forma native-analysis client (e.g. the auto Forma SDK). */
    client: FormaNativeAnalysisClient;
    /** When set, runs sun screening (reuse-first) for this date. */
    sun?: NativeSunScreeningRequest;
    /** When set, triggers noise screening. */
    noise?: NativeNoiseScreeningRequest;
  };
  /**
   * Optional live wind-comfort screening. Requires a connected `geometryClient`
   * (its meshes drive the wind height maps). When set, the pipeline rasterizes
   * the extracted geometry, runs Forma's rapid wind-comfort prediction, and
   * feeds the result into `wind-comfort` evidence. A pre-fetched `windComfort`
   * override takes precedence, so no live prediction is issued.
   */
  windScreening?: {
    /** Connected Forma wind-prediction client (e.g. the auto Forma SDK). */
    prediction: FormaWindPredictionClient;
    /** Comfort scale for the prediction; defaults to Forma's lawson_lddc. */
    comfortScale?: WindComfortScale;
  };
}

export interface LocalPilotPipelineResult {
  analyses: GenericAnalysisOutput[];
  failed: number;
  needsReview: number;
  project: Project;
  report: ReturnType<typeof buildHebrewReportShell>;
  /**
   * Serializable `report-agent-package/v1` assembled from the screening
   * evidence this run produced. Present only when the pipeline collected at
   * least one piece of simulation evidence (e.g. live geometry readiness).
   */
  reportPackage?: ReportAgentPackage;
  /** Screening summary derived from the collected evidence, when available. */
  screeningSummary?: ResultsSummary;
}

export async function runLocalPilotPipeline(
  options: RunLocalPilotPipelineOptions,
): Promise<LocalPilotPipelineResult> {
  const projectName = normalizeProjectName(options.projectName);
  const geometryClient = options.geometryClient;
  const generatedAt = new Date();
  let project = createFallbackProject(options.municipalityId, projectName);
  // Captured from the geometry stage so live wind screening can reuse the
  // extracted paths and bounds (the meshes themselves are re-fetched).
  let geometryExchange: GeometryExchange | undefined;
  const evidence: SimulationEvidence[] = [];
  const pipeline = new Pipeline<GenericAnalysisOutput>([
    {
      id: 'project-snapshot',
      async run() {
        project = await buildProjectSnapshot({
          buildingUseType: options.buildingUseType,
          forma: await resolveFormaClient(options.forma),
          municipalityId: options.municipalityId,
          projectName,
        });

        return {
          id: 'project-snapshot',
          title: 'Project snapshot',
          status: 'complete',
          summary: 'Forma project metadata and building paths are staged locally.',
          metrics: [
            { label: 'Buildings', value: project.buildings.length },
            { label: 'Building type', value: options.buildingUseType },
          ],
        };
      },
    },
    {
      id: 'municipality',
      async run() {
        return {
          id: 'municipality',
          title: 'Municipality selection',
          status: 'complete',
          summary: 'Tel Aviv pilot selection is staged locally.',
        };
      },
    },
    {
      id: 'report-shell',
      async run() {
        return {
          id: 'report-shell',
          title: 'Report shell',
          status: 'complete',
          summary: 'Hebrew report shell can receive neutral analysis outputs.',
        };
      },
    },
    {
      id: 'geometry-extraction',
      async run() {
        if (geometryClient === undefined) {
          return {
            id: 'geometry-extraction',
            title: 'Geometry extraction',
            status: 'needs-review',
            summary:
              'No live geometry client is connected; geometry extraction did not run against Forma.',
            metrics: [{ label: 'Status', value: 'not-run' }],
          } satisfies GenericAnalysisOutput;
        }

        const exchange = await buildGeometryExchange({ forma: geometryClient });
        geometryExchange = exchange;
        evidence.push(analyzeGeometryReadiness(exchange));

        return buildGeometryExtractionOutput(exchange);
      },
    },
    {
      id: 'spec-readiness',
      async run() {
        const studies = listPilotComplianceStudies({ municipalityId: options.municipalityId });
        const missingSpecs = studies.filter((study) => study.specStatus === 'missing');

        return {
          id: 'spec-readiness',
          title: 'Spec readiness',
          status: missingSpecs.length === 0 ? 'complete' : 'needs-review',
          summary:
            missingSpecs.length === 0
              ? 'All pilot Spec Mapping Documents are validated.'
              : `${missingSpecs.length} pilot studies need validated Spec Mapping Documents before regulatory logic runs.`,
          metrics: [
            { label: 'Pilot studies', value: studies.length },
            { label: 'Missing specs', value: missingSpecs.length },
          ],
          notes: missingSpecs.slice(0, 4).map((study) => `${study.title}: ${study.specPath}`),
        } satisfies GenericAnalysisOutput;
      },
    },
  ]);
  const pipelineResult = await pipeline.run();
  // Live native screening runs after the snapshot stage so it can target the
  // project's resolved building paths. A pre-fetched override wins per study, so
  // we never issue a live trigger for a study the caller already supplied.
  const liveScreening = await runNativeScreening(options, project);
  const effectiveSun = options.sunAnalysis ?? liveScreening?.sun;
  const effectiveNoise = options.noiseAnalysis ?? liveScreening?.noise;
  if (effectiveSun !== undefined) {
    evidence.push(analyzeSunShadowScreening(effectiveSun));
  }
  if (effectiveNoise !== undefined) {
    evidence.push(analyzeNoiseScreening(effectiveNoise));
  }
  // Live wind screening reuses the extracted geometry; a pre-fetched override
  // wins, so no live prediction is issued when windComfort is supplied.
  const windResult =
    options.windComfort === undefined
      ? await runWindScreening(options, geometryClient, geometryExchange)
      : undefined;
  if (options.windComfort !== undefined) {
    const { grid, comfortScale } = options.windComfort;
    evidence.push(
      comfortScale === undefined
        ? analyzeWindComfortScreening(grid)
        : analyzeWindComfortScreening(grid, { comfortScale }),
    );
  } else if (windResult?.grid !== undefined) {
    evidence.push(
      analyzeWindComfortScreening(windResult.grid, { comfortScale: windResult.comfortScale }),
    );
  }
  const analyses = [
    ...pipelineResultsToAnalyses(pipelineResult),
    ...(liveScreening === undefined ? [] : [buildNativeScreeningOutput(liveScreening)]),
    ...(windResult === undefined ? [] : [buildWindScreeningOutput(windResult)]),
  ];
  const needsReview = analyses.filter(
    (analysis) => analysis.status === 'error' || analysis.status === 'needs-review',
  ).length;
  const screening =
    evidence.length === 0
      ? undefined
      : buildReportPackageFromEvidence({
          evidence,
          project: {
            name: project.name,
            municipalityId: project.municipalityId,
            timezone: project.timezone,
          },
          municipalityId: options.municipalityId,
          language: 'he',
          generatedAt,
        });
  // The Hebrew report shell surfaces the staging analyses plus any screening
  // evidence this run produced, so reviewers see the screening results in
  // context. The returned `analyses` stay the staging set for compatibility.
  const reportAnalyses =
    screening === undefined
      ? analyses
      : [...analyses, ...collectedStudiesToReportAnalyses(screening.collected)];
  const report = buildHebrewReportShell({
    project,
    generatedAt,
    analyses: reportAnalyses,
    messages: HEBREW_REPORT_MESSAGES,
  });

  return {
    analyses,
    failed: pipelineResult.failed,
    needsReview,
    project,
    report,
    ...(screening === undefined
      ? {}
      : { reportPackage: screening.package, screeningSummary: screening.collected.summary }),
  };
}

function buildGeometryExtractionOutput(exchange: GeometryExchange): GenericAnalysisOutput {
  const { summary } = exchange;
  const status = summary.confidence === 'high' ? 'complete' : 'needs-review';
  const metrics = [
    { label: 'Buildings', value: summary.buildingCount },
    { label: 'Footprints', value: summary.footprintCount },
    { label: 'Confidence', value: summary.confidence },
  ];
  const notes = summary.missingData
    .slice(0, 4)
    .map((field) => `Missing geometry evidence: ${field}`);

  return {
    id: 'geometry-extraction',
    title: 'Geometry extraction',
    status,
    summary:
      status === 'complete'
        ? `Extracted ${summary.buildingCount} building(s) from the live Forma model with high-confidence geometry.`
        : `Extracted ${summary.buildingCount} building(s) from the live Forma model; geometry needs review (${summary.confidence} confidence).`,
    metrics,
    ...(notes.length === 0 ? {} : { notes }),
  } satisfies GenericAnalysisOutput;
}

/**
 * Runs the requested live native screenings against the project's resolved
 * building paths. Returns undefined when nothing live is requested (either no
 * `nativeScreening` option, or every requested study already has a pre-fetched
 * override). An empty selection degrades to captured per-study failures inside
 * the result rather than throwing.
 */
async function runNativeScreening(
  options: RunLocalPilotPipelineOptions,
  project: Project,
): Promise<NativeScreeningResult | undefined> {
  const request = options.nativeScreening;
  if (request === undefined) {
    return undefined;
  }

  const sun = options.sunAnalysis === undefined ? request.sun : undefined;
  const noise = options.noiseAnalysis === undefined ? request.noise : undefined;
  if (sun === undefined && noise === undefined) {
    return undefined;
  }

  const selectedElementPaths = [
    ...new Set(project.buildings.flatMap((building) => [...building.elementPaths])),
  ];

  return collectNativeScreening({
    forma: request.client,
    selectedElementPaths,
    ...(sun === undefined ? {} : { sun }),
    ...(noise === undefined ? {} : { noise }),
  });
}

/**
 * Runs live wind-comfort screening when requested and a geometry exchange is
 * available. Returns undefined when nothing wind-related is requested (no
 * `windScreening`, no geometry, or a pre-fetched override). A missing center or
 * empty geometry degrades to a captured failure rather than throwing.
 */
async function runWindScreening(
  options: RunLocalPilotPipelineOptions,
  geometryClient: FormaGeometryExchangeClient | undefined,
  geometryExchange: GeometryExchange | undefined,
): Promise<WindComfortScreeningResult | undefined> {
  const request = options.windScreening;
  if (request === undefined || geometryClient === undefined || geometryExchange === undefined) {
    return undefined;
  }

  const center = geometryCenter(geometryExchange);
  const buildingPaths = geometryExchange.elements.map((element) => element.path);
  if (center === undefined || buildingPaths.length === 0) {
    return {
      comfortScale: request.comfortScale ?? DEFAULT_WIND_COMFORT_SCALE,
      failure: 'No extracted geometry bounds for wind height maps.',
    };
  }

  const rootUrn = geometryExchange.project.rootUrn;
  // Terrain is read separately (the geometry exchange only extracts buildings)
  // so the height maps reflect real ground instead of a flat baseline. Missing
  // terrain degrades to flat ground inside the rasterizer.
  const terrainPaths = await fetchTerrainPaths(geometryClient.geometry, rootUrn);

  return collectWindComfortScreening({
    geometry: geometryClient.geometry,
    prediction: request.prediction,
    buildingPaths,
    ...(terrainPaths.length === 0 ? {} : { terrainPaths }),
    rootUrn,
    center,
    ...(request.comfortScale === undefined ? {} : { comfortScale: request.comfortScale }),
  });
}

const WIND_TERRAIN_CATEGORY = 'terrain';

async function fetchTerrainPaths(
  geometry: FormaGeometryExchangeClient['geometry'],
  rootUrn: string,
): Promise<string[]> {
  try {
    return await geometry.getPathsByCategory({ category: WIND_TERRAIN_CATEGORY, urn: rootUrn });
  } catch {
    return [];
  }
}

function geometryCenter(exchange: GeometryExchange): { x: number; y: number } | undefined {
  const bbox = exchange.summary.bbox;
  if (bbox === undefined) {
    return undefined;
  }

  return { x: (bbox.minX + bbox.maxX) / 2, y: (bbox.minY + bbox.maxY) / 2 };
}

function buildWindScreeningOutput(result: WindComfortScreeningResult): GenericAnalysisOutput {
  const succeeded = result.grid !== undefined;

  return {
    id: 'wind-screening',
    title: 'Wind comfort screening',
    status: succeeded ? 'complete' : 'needs-review',
    summary: succeeded
      ? `Predicted rapid wind comfort live (${result.comfortScale} scale).`
      : `Wind comfort screening could not run (${result.comfortScale} scale).`,
    metrics: [{ label: 'Comfort scale', value: result.comfortScale }],
    ...(result.failure === undefined ? {} : { notes: [result.failure] }),
  } satisfies GenericAnalysisOutput;
}

function buildNativeScreeningOutput(result: NativeScreeningResult): GenericAnalysisOutput {
  const succeeded = (result.sun === undefined ? 0 : 1) + (result.noise === undefined ? 0 : 1);
  const failed = result.failures.length;
  // A trigger that did not throw is not the same as a finished analysis: a
  // freshly triggered sun/noise run comes back in progress with no ground grid,
  // which the evidence analyzers flag as needs-review. Reflect that here so this
  // summary status does not contradict the report body.
  const pendingEvidence = [
    ...(result.sun === undefined ? [] : [analyzeSunShadowScreening(result.sun)]),
    ...(result.noise === undefined ? [] : [analyzeNoiseScreening(result.noise)]),
  ].filter((evidence) => evidence.status !== 'screening-ready');
  const needsReview = failed > 0 || pendingEvidence.length > 0;
  const notes = [
    ...result.failures.map((failure) => `${failure.studyId}: ${failure.message}`),
    ...pendingEvidence.map((evidence) => `${evidence.studyId}: ${evidence.status}`),
  ];

  let summary: string;
  if (failed > 0) {
    summary = `${succeeded} native screening analysis(es) ran; ${failed} could not run.`;
  } else if (pendingEvidence.length > 0) {
    summary = `Triggered ${succeeded} native screening analysis(es); ${pendingEvidence.length} need review (results pending).`;
  } else {
    summary = `Triggered ${succeeded} native screening analysis(es) live from Forma.`;
  }

  return {
    id: 'native-screening',
    title: 'Native analysis screening',
    status: needsReview ? 'needs-review' : 'complete',
    summary,
    metrics: [
      { label: 'Succeeded', value: succeeded },
      { label: 'Failed', value: failed },
    ],
    ...(notes.length === 0 ? {} : { notes }),
  } satisfies GenericAnalysisOutput;
}

async function resolveFormaClient(
  forma: FormaProjectSnapshotClient | undefined,
): Promise<FormaProjectSnapshotClient> {
  if (forma !== undefined) {
    return forma;
  }

  return loadAutoFormaProjectSnapshotClient();
}

function normalizeProjectName(projectName: string | undefined): string {
  const normalized = projectName?.trim();

  return normalized === undefined || normalized.length === 0 ? 'EnerSU pilot' : normalized;
}

function createFallbackProject(municipalityId: MunicipalityId, projectName: string): Project {
  return {
    id: 'local-pilot',
    name: projectName,
    timezone: 'Asia/Jerusalem',
    municipalityId,
    site: {
      id: 'local-site',
      terrainPaths: [],
      boundaryPaths: [],
    },
    buildings: [],
  };
}
